import get from "lodash.get";
import moment from "moment";

import ParkkiTheme from "themes/ParkkiTheme";
import Abilities from "services/permissions/abilities";
import CommonUtils from "services/common/utils";

class CustomAnalysisUtils {
  /**
   * Subtract two months from the given date and return the unix timestamp of the date with the same day of the week
   * @param {number} dayToFind - The day of the week to find (0-6)
   * @param {moment} date - The date to subtract two months from
   * @returns {number} - The unix timestamp of the date with the same day of the week two months before divided by 1000
   */
  static subtractTwoMonthToSameDay = (dayToFind, date) => {
    return date
      .clone()
      .subtract(2, "month")
      .startOf("day")
      .day(dayToFind)
      .unix();
  };

  static getBodyFromStateAndType = (state, type = "analysis") => {
    const from = get(state.CustomAnalysis, "filters.from");
    const to = get(state.CustomAnalysis, "filters.to");
    const comparedFrom = get(state.CustomAnalysis, "filters.comparedFrom");
    const comparedTo = parseInt(
      CustomAnalysisUtils.getComparedTo(
        from * 1000,
        to * 1000,
        comparedFrom * 1000
      ) / 1000
    );

    switch (type) {
      case "analysis":
        return {
          from,
          to,
          areas: get(state.CustomAnalysis, "filters.selectedAreas"),
        };
      case "compared":
        return {
          from: comparedFrom,
          to: comparedTo,
          areas: get(state.CustomAnalysis, "filters.selectedAreas"),
        };
      default:
        return {};
    }
  };

  static formatAreaFromApi = (area, config = []) => {
    const areaConfig = config.find(
      (config) =>
        get(area, "id", get(area, "area_id")) ===
        get(config, "area_id", get(config, "id"))
    );

    return {
      ...areaConfig,
      ...area,
    };
  };

  static formatAreasFromApi = (areas = [], config) => {
    const areasToDisplay = areas.filter((area) =>
      config.find(
        (areaConfig) => areaConfig?.area_id && areaConfig?.area_id === area?.id
      )
    );
    return areasToDisplay.map((area) =>
      CustomAnalysisUtils.formatAreaFromApi(area, config)
    );
  };

  static formatLayersInfosFromApi = (
    layersInfos,
    config,
    currentSelectedAreas = [],
    allowedTypes = []
  ) => {
    const areas = CustomAnalysisUtils.formatAreasFromApi(
      get(layersInfos, "areas", []),
      get(config, "areas", [])
    );

    const currentSelectedAreasTypes = new Set(
      areas
        .filter((area) => currentSelectedAreas.includes(area.id))
        .map((area) => area.type)
    );

    return {
      ...layersInfos,
      areas,
      selectedAreas:
        currentSelectedAreas.length > 0 && currentSelectedAreasTypes.size === 1
          ? currentSelectedAreas
          : areas
              .filter((area) =>
                allowedTypes.length > 0
                  ? allowedTypes.includes(area.type)
                  : true
              )
              .map((area) => area.id),
    };
  };

  static getComparedTo = (from, to, comparedFrom) => {
    const diffDays = moment(to)
      .startOf("day")
      .diff(moment(from).startOf("day"), "days");
    const comparedTo = moment(comparedFrom)
      .startOf("day")
      .add(diffDays, "d")
      .endOf("day");

    return parseInt(comparedTo.endOf("day").valueOf());
  };

  static getColorFromType = (type) => {
    switch (type) {
      case "attendance":
        return ParkkiTheme.colors.lightBlue;
      case "attendancePeak":
        return ParkkiTheme.colors.blue;
      case "dowAttendance":
        return ParkkiTheme.colors.lightBlue;
      case "dowPressure":
        return ParkkiTheme.colors.turquoise;
      case "hourlyAttendance":
        return ParkkiTheme.colors.turquoise3;
      case "pressure":
        return ParkkiTheme.colors.turquoise2;
      default:
        return ParkkiTheme.colors.lightBlue;
    }
  };

  static getChartOptions = (graph, intl, comparedLabels, legend, state) => {
    const groupType = CustomAnalysisUtils.getGroupTypeFromFilters(
      state.filters
    );

    return {
      maintainAspectRatio: false,
      responsive: true,
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
        },
      },
      title: {
        display: !!legend,
        text: legend,
        position: "bottom",
        fontStyle: "normal",
      },
      tooltips: {
        enabled: true,
        mode: "index",
        filter(tooltipItem, data) {
          const currentGraph = get(
            data,
            `datasets[${tooltipItem.datasetIndex}]`
          );

          // Hidding tooltip of record
          if (
            get(currentGraph, "yLabel") === "all_time_record" ||
            get(currentGraph, "yLabel") === "all_time_average"
          ) {
            return false;
          }

          return true;
        },
        callbacks: {
          title: (tooltipItems, data) => {
            if (graph === "attendancePeak" && tooltipItems.length < 3)
              return "";
            if (graph !== "attendancePeak" && tooltipItems.length !== 2)
              return "";

            let value = get(tooltipItems, "[1].yLabel");
            let compared = get(tooltipItems, "[0].yLabel");
            if (graph === "attendancePeak") {
              value = get(tooltipItems, "[2].yLabel");
              compared = get(tooltipItems, "[1].yLabel");
            }

            let percentage = Math.round(((compared - value) / value) * 100);
            if (get(data, "datasets", []).length === 1) {
              return "";
            }

            if (graph === "dowPressure") {
              percentage = compared - value;
            }

            return `${percentage > 0 ? "+" : ""}${CommonUtils.formatNumber(
              percentage
            )} %`;
          },
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset = datasetIndex >= 0 && datasets[datasetIndex];
            const value =
              (currentDataset &&
                currentDataset.data &&
                currentDataset.data[index]) ||
              intl.messages["parkStatisticsScene.chart.any"];

            const currentGraph = data?.datasets?.[tooltipItem.datasetIndex];

            if (
              graph === "attendancePeak" &&
              currentGraph?.yLabel === "capacity"
            ) {
              return `${
                intl.messages[
                  `customAnalysisScene.filters.analysisAreaGroups.capacity`
                ]
              }: ${CommonUtils.formatNumber(value)} ${
                intl.messages[
                  `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
                    0,
                    -1
                  )}${value > 1 ? "s" : ""}`
                ]
              }`;
            }

            if (graph === "hourlyAttendance") {
              return `${CommonUtils.humanizeDateFromDuration(
                value,
                "seconds",
                intl,
                false
              )} (${get(tooltipItem, "label")})`;
            }

            if (graph === "dowPressure") {
              return `${CommonUtils.formatNumber(value)} %`;
            }
            if (graph === "dowAttendance") {
              return `${CommonUtils.formatNumber(value)} ${
                intl.messages[
                  `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
                    0,
                    -1
                  )}${value > 1 ? "s" : ""}`
                ]
              }`;
            }

            if (
              get(data, "datasets", []).length === 1 ||
              get(tooltipItem, "datasetIndex") === 1
            ) {
              return `${CommonUtils.formatNumber(value)} ${
                intl.messages[
                  `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
                    0,
                    -1
                  )}${value > 1 ? "s" : ""}`
                ]
              } (${get(tooltipItem, "label")})`;
            }

            if (
              graph === "attendance" ||
              graph === "attendancePeak" ||
              graph === "hourlyAttendance"
            ) {
              let date;

              if (get(currentDataset, "yLabel") === "analysis") {
                date = get(tooltipItem, "label");
              } else {
                date = get(comparedLabels, [get(tooltipItem, "index", 0)]);
              }

              return `${CommonUtils.formatNumber(value)} ${
                intl.messages[
                  `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
                    0,
                    -1
                  )}${value > 1 ? "s" : ""}`
                ]
              } (${date})`;
            }

            return `${CommonUtils.formatNumber(value)}`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            categoryPercentage: 0.9,
            barPercentage: 0.95,
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: (tickValue) => {
                if (graph === "hourlyAttendance") {
                  return CommonUtils.humanizeDateFromDuration(
                    tickValue,
                    "seconds",
                    intl,
                    false
                  );
                }
                return tickValue;
              },
            },
          },
        ],
      },
    };
  };

  static getChartByDayOfWeekData = (graph, state, type) =>
    get(state, `${graph}.${type}.data`, []).map((item) => get(item, "y"));

  static getLineChartData = (graph, state, type, accessor) =>
    new Array(get(state, `${graph}.${type}.data`, []).length).fill(
      get(state, `${graph}.${type}.${accessor}`)
    );

  static getChartByDayOfWeek = (graph, state, intl) => {
    const withComparison = get(state, "filters.withComparison", false);

    return {
      chart: {
        toggle: Abilities.ability.can("read", `customAnalysis.${graph}`),
        labels: this.getChartLabels(graph, "day", state, intl),
        datasets: [
          {
            type: "bar",
            label:
              intl.messages[`customAnalysisScene.sections.${graph}.analysis`],
            data: CustomAnalysisUtils.getChartByDayOfWeekData(
              graph,
              state,
              "analysis"
            ),
            backgroundColor: CustomAnalysisUtils.getColorFromType(graph),
            borderColor: CustomAnalysisUtils.getColorFromType(graph),
          },
          ...(withComparison
            ? [
                {
                  type: "bar",
                  label:
                    intl.messages[
                      `customAnalysisScene.sections.${graph}.compared`
                    ],
                  data: CustomAnalysisUtils.getChartByDayOfWeekData(
                    graph,
                    state,
                    "compared"
                  ),
                  backgroundColor: ParkkiTheme.colors.orange,
                  borderColor: ParkkiTheme.colors.orange,
                },
              ]
            : []),
        ],
      },
      options: CustomAnalysisUtils.getChartOptions(
        graph,
        intl,
        this.getChartLabels(graph, "day", state, intl, "compared"),
        null,
        state
      ),
    };
  };

  static getChartLabels = (
    graph,
    dimension,
    state,
    intl,
    type = "analysis"
  ) => {
    if (graph === "hourlyAttendance") {
      const allowedLabels = new Array(24)
        .fill(0)
        .map((item, index) => `${index < 10 ? "0" : ""}${index}:00`);
      const comparedLabels = get(state, `${graph}.analysis.data`, []).map(
        (item) => get(item, "x")
      );
      const analysisLabels = get(state, `${graph}.compared.data`, []).map(
        (item) => get(item, "x")
      );

      const unionOfLabels = [
        ...new Set([...comparedLabels, ...analysisLabels]),
      ];
      return allowedLabels.filter((allowedLabel) =>
        unionOfLabels.includes(allowedLabel)
      );
    }

    if (dimension === "hours") {
      return get(state, `${graph}.${type}.data`, []).map((item) =>
        moment(get(item, "x")).format("LLL")
      );
    }
    if (dimension === "day") {
      return get(state, `${graph}.${type}.data`, []).map(
        (item) => intl.messages[`daysOfWeek.${get(item, "x") - 1}`]
      );
    }
    if (dimension === "days") {
      return get(state, `${graph}.${type}.data`, []).map((item) =>
        moment(get(item, "x")).format("ddd DD/MM")
      );
    }
    if (dimension === "weeks") {
      return get(state, `${graph}.${type}.data`, []).map(
        (item) =>
          `${intl.messages["calendar.shortWeek"]}${moment(
            get(item, "x"),
            "YYYY w"
          ).format("w gggg")}`
      );
    }
    if (dimension === "months") {
      return get(state, `${graph}.${type}.data`, []).map((item) =>
        moment(get(item, "x"), "YYYY-MM").format("MMM YYYY")
      );
    }
  };

  static getAttendanceChart = (graph, dimension, state, intl) => {
    const withComparison = get(state, "filters.withComparison", false);

    return {
      chart: {
        toggle: Abilities.ability.can("read", `customAnalysis.${graph}`),
        labels: this.getChartLabels(graph, dimension, state, intl),
        datasets: [
          ...(graph === "attendance" || graph === "hourlyAttendance"
            ? [
                {
                  type: "line",
                  label:
                    intl.messages[
                      `customAnalysisScene.sections.${graph}.allTimeAverage`
                    ],
                  yLabel: "all_time_average",
                  data: CustomAnalysisUtils.getLineChartData(
                    graph,
                    state,
                    "analysis",
                    "all_time_average"
                  ),
                  fill: false,
                  borderColor: ParkkiTheme.colors.turquoise2,
                  backgroundColor: "rgba(255, 255, 255, 0)",
                  borderDash: [5, 5],
                  pointHoverRadius: 0,
                  pointHoverBorderWidth: 0,
                  pointRadius: 0,
                  borderWidth: 1,
                },
              ]
            : []),
          ...(graph === "attendance" ||
          graph === "attendancePeak" ||
          graph === "hourlyAttendance"
            ? [
                {
                  type: "line",
                  label:
                    intl.messages[
                      `customAnalysisScene.sections.${graph}.allTimeRecord`
                    ],
                  yLabel: "all_time_record",
                  data: CustomAnalysisUtils.getLineChartData(
                    graph,
                    state,
                    "analysis",
                    "all_time_record.value"
                  ),
                  fill: false,
                  borderColor: ParkkiTheme.colors.green,
                  backgroundColor: "rgba(255, 255, 255, 0)",
                  borderDash: [5, 5],
                  pointHoverRadius: 0,
                  pointHoverBorderWidth: 0,
                  pointRadius: 0,
                  borderWidth: 1,
                  hidden: true,
                },
              ]
            : []),
          ...(graph === "attendancePeak"
            ? [
                {
                  type: "line",
                  label:
                    intl.messages[
                      "customAnalysisScene.sections.attendancePeak.capacity"
                    ],
                  yLabel: "capacity",
                  data: CustomAnalysisUtils.getLineChartData(
                    graph,
                    state,
                    "analysis",
                    "capacity"
                  ),
                  fill: false,
                  borderColor: ParkkiTheme.colors.grey,
                  backgroundColor: ParkkiTheme.colors.grey,
                  borderDash: [5, 5],
                  pointHoverRadius: 0,
                  pointHoverBorderWidth: 0,
                  pointRadius: 0,
                  borderWidth: 1,
                },
              ]
            : []),
          {
            type: "bar",
            label:
              intl.messages[`customAnalysisScene.sections.${graph}.analysis`],
            yLabel: "analysis",
            data: CustomAnalysisUtils.getChartByDayOfWeekData(
              graph,
              state,
              "analysis"
            ),
            backgroundColor: CustomAnalysisUtils.getColorFromType(graph),
            borderColor: CustomAnalysisUtils.getColorFromType(graph),
          },
          ...(withComparison
            ? [
                {
                  type: "bar",
                  label:
                    intl.messages[
                      `customAnalysisScene.sections.${graph}.compared`
                    ],
                  yLabel: "compared",
                  data: CustomAnalysisUtils.getChartByDayOfWeekData(
                    graph,
                    state,
                    "compared"
                  ),
                  backgroundColor: ParkkiTheme.colors.orange,
                  borderColor: ParkkiTheme.colors.orange,
                },
              ]
            : []),
        ],
      },
      options: CustomAnalysisUtils.getChartOptions(
        graph,
        intl,
        this.getChartLabels(graph, dimension, state, intl, "compared"),
        this.getLegend(graph, state, intl),
        state
      ),
    };
  };

  static getGroupTypeFromFilters = (filters) => {
    return (
      filters?.analysisAreaGroups?.selected?.[0] ||
      filters?.analysisAreaGroups?.default ||
      "cars"
    );
  };

  static getLegend = (graph, state, intl) => {
    if (graph === "attendance") {
      const allTimeAverage = state?.[graph]?.analysis?.all_time_average ?? "-";
      const allTimeRecord =
        state?.[graph]?.analysis?.all_time_record?.value ?? "-";
      const groupType = CustomAnalysisUtils.getGroupTypeFromFilters(
        state.filters
      );

      return `${
        intl.messages[`customAnalysisScene.sections.${graph}.allTimeAverage`]
      } : ${CommonUtils.formatNumber(allTimeAverage)} ${
        intl.messages[
          `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
            0,
            -1
          )}${allTimeAverage > 1 ? "s" : ""}`
        ]
      } - ${
        intl.messages[`customAnalysisScene.sections.${graph}.allTimeRecord`]
      } : ${CommonUtils.formatNumber(allTimeRecord)} ${
        intl.messages[
          `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
            0,
            -1
          )}${allTimeRecord > 1 ? "s" : ""}`
        ]
      }`;
    }

    if (graph === "attendancePeak") {
      const allTimeRecord =
        state?.[graph]?.analysis?.all_time_record?.value ?? "-";
      const groupType = CustomAnalysisUtils.getGroupTypeFromFilters(
        state.filters
      );

      return `${
        intl.messages[`customAnalysisScene.sections.${graph}.allTimeRecord`]
      } : ${CommonUtils.formatNumber(allTimeRecord)} ${
        intl.messages[
          `customAnalysisScene.filters.analysisAreaGroups.${groupType.slice(
            0,
            -1
          )}${allTimeRecord > 1 ? "s" : ""}`
        ]
      }`;
    }

    if (graph === "hourlyAttendance") {
      const allTimeAverage = state?.[graph]?.analysis?.all_time_average ?? "-";
      const allTimeRecord =
        state?.[graph]?.analysis?.all_time_record?.value ?? "-";

      return `${
        intl.messages[`customAnalysisScene.sections.${graph}.allTimeAverage`]
      } : ${CommonUtils.humanizeDateFromDuration(
        allTimeAverage,
        "seconds",
        intl,
        false
      )} - ${
        intl.messages[`customAnalysisScene.sections.${graph}.allTimeRecord`]
      } : ${CommonUtils.humanizeDateFromDuration(
        allTimeRecord,
        "seconds",
        intl,
        false
      )}`;
    }

    return null;
  };

  static getWay = (value, avg) => {
    const diff = value - avg;

    if (diff > 0) {
      return 1;
    }
    if (diff < 0) {
      return -1;
    }

    return 0;
  };

  static getColor = (value, avg) => {
    const way = CustomAnalysisUtils.getWay(value, avg);
    return way >= 0 ? "green" : "red";
  };

  static getStatsRow = (state, intl) => {
    const withComparison = get(state, "filters.withComparison", false);

    if (
      !Abilities.ability.can(
        "read",
        "customAnalysis.attendanceStats.avgAttendance"
      ) &&
      !Abilities.ability.can(
        "read",
        "customAnalysis.attendanceStats.recordAttendance"
      ) &&
      !Abilities.ability.can(
        "read",
        "customAnalysis.attendanceStats.recordPeakAttendance"
      )
    ) {
      return null;
    }

    return [
      {
        toggle: Abilities.ability.can(
          "read",
          "customAnalysis.attendanceStats.avgAttendance"
        ),
        label:
          intl.messages[
            "customAnalysisScene.sections.statsRow.avgAttendance.label"
          ],
        subLabel: "",
        value: {
          value: get(state, "attendanceStats.analysis.avg", "-"),
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          toggle:
            Abilities.ability.can(
              "read",
              "customAnalysis.attendanceStats.avgAttendance"
            ) && withComparison,
          name: "percentage",
          retrieveSubvalue: () =>
            get(state, "attendanceStats.compared.avg", "-"),
          subvalueLabel: "",
          retrieveColor: () => "orange",
          symbol: "",
          retrievePercentage: (value, subvalue) =>
            ((value - subvalue) / subvalue) * 100,
          formatPercentage: (v) => Math.round(v * 100) / 100,
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "customAnalysis.attendanceStats.recordAttendance"
        ),
        label:
          intl.messages[
            "customAnalysisScene.sections.statsRow.recordAttendance.label"
          ],
        subLabel: intl.formatMessage(
          { id: "customAnalysisScene.sections.statsRow.the" },
          {
            day: moment(
              get(
                state,
                "attendanceStats.analysis.period_attendance_record.date"
              ),
              "YYYY-MM-DD"
            ).format("LL"),
          }
        ),
        value: {
          value: get(
            state,
            "attendanceStats.analysis.period_attendance_record.value",
            "-"
          ),
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          toggle:
            Abilities.ability.can(
              "read",
              "customAnalysis.attendanceStats.recordAttendance"
            ) && withComparison,
          name: "percentage",
          retrieveSubvalue: () =>
            get(
              state,
              "attendanceStats.compared.period_attendance_record.value",
              "-"
            ),
          subvalueLabel: intl.formatMessage(
            { id: "customAnalysisScene.sections.statsRow.the" },
            {
              day: moment(
                get(
                  state,
                  "attendanceStats.compared.period_attendance_record.date"
                ),
                "YYYY-MM-DD"
              ).format("LL"),
            }
          ),
          retrieveColor: () => "orange",
          symbol: "",
          retrievePercentage: (value, subvalue) =>
            ((value - subvalue) / subvalue) * 100,
          formatPercentage: (v) => Math.round(v * 100) / 100,
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "customAnalysis.attendanceStats.recordPeakAttendance"
        ),
        label:
          intl.messages[
            "customAnalysisScene.sections.statsRow.recordPeakAttendance.label"
          ],
        subLabel: intl.formatMessage(
          { id: "customAnalysisScene.sections.statsRow.theAt" },
          {
            day: moment(
              get(state, "attendanceStats.analysis.period_peak_record.date"),
              "YYYY-MM-DD"
            ).format("LL"),
            hour: moment(
              get(state, "attendanceStats.analysis.period_peak_record.date")
            ).format("H:mm"),
          }
        ),
        value: {
          value: get(
            state,
            "attendanceStats.analysis.period_peak_record.value",
            "-"
          ),
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          toggle:
            Abilities.ability.can(
              "read",
              "customAnalysis.attendanceStats.recordPeakAttendance"
            ) && withComparison,
          name: "percentage",
          retrieveSubvalue: () =>
            get(
              state,
              "attendanceStats.compared.period_peak_record.value",
              "-"
            ),
          subvalueLabel: intl.formatMessage(
            { id: "customAnalysisScene.sections.statsRow.theAt" },
            {
              day: moment(
                get(state, "attendanceStats.compared.period_peak_record.date"),
                "YYYY-MM-DD"
              ).format("LL"),
              hour: moment(
                get(state, "attendanceStats.compared.period_peak_record.date")
              ).format("H:mm"),
            }
          ),
          retrieveColor: () => "orange",
          symbol: "",
          retrievePercentage: (value, subvalue) =>
            ((value - subvalue) / subvalue) * 100,
          formatPercentage: (v) => Math.round(v * 100) / 100,
        },
      },
    ];
  };
}

export default CustomAnalysisUtils;
